import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import { AppointmentCreate, AppointmentType } from '../../types/appointment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { fetchCachedTeamMembers, type TeamMember } from '../../utils/teamMembersCache';

interface Practice {
  uuid: string;
  name: string;
}

interface PetOwner {
  uuid: string;
  full_name: string;
  email?: string;
  phone?: string;
}

interface Pet {
  id: string;
  name: string;
  species: string;
  breed?: string;
}

export const AppointmentForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Format date for input
  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Form data - default to user's practice
  const [formData, setFormData] = useState<AppointmentCreate>({
    practice_id: user?.practice_id || '',
    pet_owner_id: '',
    assigned_vet_user_id: user?.id || '',
    appointment_date: '',
    duration_minutes: 30,
    appointment_type: AppointmentType.CHECKUP,
    title: '',
    description: '',
    notes: '',
    pet_ids: []
  });

  // Dropdown data
  const [practices, setPractices] = useState<Practice[]>([]);
  const [petOwners, setPetOwners] = useState<PetOwner[]>([]);
  const [pets, setPets] = useState<Pet[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  // Pet owner search
  const [petOwnerSearch, setPetOwnerSearch] = useState('');
  const [showPetOwnerDropdown, setShowPetOwnerDropdown] = useState(false);
  const [filteredPetOwners, setFilteredPetOwners] = useState<PetOwner[]>([]);
  const petOwnerDropdownRef = useRef<HTMLDivElement>(null);

  // Date picker
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load pets when pet owner changes
  useEffect(() => {
    if (formData.pet_owner_id) {
      loadPetsForOwner(formData.pet_owner_id);
    } else {
      setPets([]);
      setFormData(prev => ({ ...prev, pet_ids: [] }));
    }
  }, [formData.pet_owner_id]);

  // Filter pet owners based on search
  useEffect(() => {
    if (petOwnerSearch.trim() === '') {
      setFilteredPetOwners(petOwners);
    } else {
      const searchLower = petOwnerSearch.toLowerCase();
      setFilteredPetOwners(
        petOwners.filter(owner =>
          owner.full_name.toLowerCase().includes(searchLower) ||
          owner.email?.toLowerCase().includes(searchLower) ||
          owner.phone?.includes(petOwnerSearch)
        )
      );
    }
  }, [petOwnerSearch, petOwners]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (petOwnerDropdownRef.current && !petOwnerDropdownRef.current.contains(event.target as Node)) {
        setShowPetOwnerDropdown(false);
      }
    };

    if (showPetOwnerDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPetOwnerDropdown]);

  const loadInitialData = async () => {
    try {
      const [practicesRes, petOwnersRes] = await Promise.all([
        fetch(API_ENDPOINTS.PRACTICES.LIST),
        fetch(API_ENDPOINTS.PET_OWNERS.LIST)
      ]);

      if (practicesRes.ok) {
        const practicesData = await practicesRes.json();
        setPractices(practicesData);
      }

      if (petOwnersRes.ok) {
        const petOwnersData = await petOwnersRes.json();
        setPetOwners(petOwnersData);
      }

      // Load team members (with caching)
      if (user?.practice_id) {
        const token = localStorage.getItem('access_token');
        const members = await fetchCachedTeamMembers(user.practice_id, token);
        setTeamMembers(members);
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load form data');
    }
  };

  const loadPetsForOwner = async (ownerId: string) => {
    try {
      const response = await fetch(API_ENDPOINTS.PETS.BY_OWNER(ownerId));
      if (response.ok) {
        const petsData = await response.json();
        setPets(petsData);
      } else {
        setPets([]);
      }
    } catch (err) {
      console.error('Error loading pets:', err);
      setPets([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'duration_minutes' ? parseInt(value) || 0 : value
    }));
  };

  const handlePetSelection = (petId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      pet_ids: checked
        ? [...prev.pet_ids, petId]
        : prev.pet_ids.filter(id => id !== petId)
    }));
  };

  // Generate dynamic title based on pet names, appointment type, and date/time
  const generateTitle = () => {
    const selectedPets = pets.filter(p => formData.pet_ids.includes(p.id));
    const petNames = selectedPets.map(p => p.name).join(', ') || 'Pet';

    const appointmentTypeLabel = formData.appointment_type
      ? formData.appointment_type.charAt(0).toUpperCase() + formData.appointment_type.slice(1)
      : 'Appointment';

    let dateTimeStr = '';
    if (formData.appointment_date) {
      const date = new Date(formData.appointment_date);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      dateTimeStr = ` - ${dateStr} at ${timeStr}`;
    }

    return `${petNames} ${appointmentTypeLabel}${dateTimeStr}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Generate dynamic title and convert appointment_date to ISO format
      const appointmentData = {
        ...formData,
        title: generateTitle(),
        appointment_date: new Date(formData.appointment_date).toISOString()
      };

      const response = await fetch(API_ENDPOINTS.APPOINTMENTS.CREATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create appointment');
      }

      navigate('/dashboard/appointments');
    } catch (err) {
      console.error('Error creating appointment:', err);
      setError(err instanceof Error ? err.message : 'Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Custom DatePicker Styling */}
      <style>{`
        .react-datepicker {
          font-size: 1rem !important;
          border: 1px solid #d1d5db !important;
          border-radius: 0.5rem !important;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1) !important;
        }
        .react-datepicker__header {
          background-color: #f9fafb !important;
          border-bottom: 1px solid #e5e7eb !important;
          padding-top: 1rem !important;
        }
        .react-datepicker__current-month {
          font-size: 1.125rem !important;
          font-weight: 600 !important;
          color: #111827 !important;
          margin-bottom: 0.5rem !important;
        }
        .react-datepicker__day-name {
          width: 2.5rem !important;
          line-height: 2.5rem !important;
          margin: 0.25rem !important;
          font-size: 0.875rem !important;
          font-weight: 600 !important;
          color: #6b7280 !important;
        }
        .react-datepicker__day {
          width: 2.5rem !important;
          line-height: 2.5rem !important;
          margin: 0.25rem !important;
          font-size: 0.875rem !important;
          border-radius: 0.375rem !important;
        }
        .react-datepicker__day:hover {
          background-color: #dbeafe !important;
          border-radius: 0.375rem !important;
        }
        .react-datepicker__day--selected {
          background-color: #3b82f6 !important;
          color: white !important;
          font-weight: 600 !important;
        }
        .react-datepicker__time-container {
          border-left: 1px solid #e5e7eb !important;
        }
        .react-datepicker__time-list-item {
          font-size: 0.875rem !important;
          padding: 0.5rem !important;
        }
        .react-datepicker__time-list-item:hover {
          background-color: #dbeafe !important;
        }
        .react-datepicker__time-list-item--selected {
          background-color: #3b82f6 !important;
          color: white !important;
          font-weight: 600 !important;
        }
      `}</style>

      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Schedule New Appointment</h1>
        <p className="text-gray-600 mt-1">Create a new appointment for a pet owner</p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Practice (hidden, uses current user's practice) */}
          <input type="hidden" name="practice_id" value={formData.practice_id} />

          {/* Pet Owner Search */}
          <div className="relative" ref={petOwnerDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pet Owner *
            </label>
            <input
              type="text"
              value={petOwnerSearch}
              onChange={(e) => {
                setPetOwnerSearch(e.target.value);
                setShowPetOwnerDropdown(true);
              }}
              onFocus={() => setShowPetOwnerDropdown(true)}
              placeholder="Search by name, email, or phone..."
              required={!formData.pet_owner_id}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />

            {/* Dropdown */}
            {showPetOwnerDropdown && filteredPetOwners.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredPetOwners.map(owner => (
                  <button
                    key={owner.uuid}
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, pet_owner_id: owner.uuid }));
                      setPetOwnerSearch(owner.full_name);
                      setShowPetOwnerDropdown(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-0"
                  >
                    <div className="font-medium text-gray-900">{owner.full_name}</div>
                    {(owner.email || owner.phone) && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {owner.email && <span>{owner.email}</span>}
                        {owner.email && owner.phone && <span className="mx-1">•</span>}
                        {owner.phone && <span>{owner.phone}</span>}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Pet Selection */}
          {pets.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Pets *
              </label>
              <div className="space-y-2">
                {pets.map(pet => (
                  <label key={pet.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.pet_ids.includes(pet.id)}
                      onChange={(e) => handlePetSelection(pet.id, e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-3 text-sm text-gray-900">
                      {pet.name} ({pet.species}{pet.breed ? `, ${pet.breed}` : ''})
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date & Time *
              </label>
              <DatePicker
                selected={selectedDate}
                onChange={(date: Date | null) => {
                  setSelectedDate(date);
                  if (date) {
                    setFormData(prev => ({
                      ...prev,
                      appointment_date: formatDateForInput(date)
                    }));
                  }
                }}
                showTimeSelect
                timeIntervals={15}
                dateFormat="MM/dd/yyyy, hh:mm aa"
                minDate={new Date()}
                required
                className="w-full px-3 py-2 text-base text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white cursor-pointer hover:border-gray-400 transition-colors"
                wrapperClassName="w-full"
                placeholderText="Select date and time"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration *
              </label>
              <select
                name="duration_minutes"
                value={formData.duration_minutes}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">1 hour</option>
                <option value="90">1 hour 30 minutes</option>
                <option value="120">2 hours</option>
              </select>
            </div>
          </div>

          {/* Appointment Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Appointment Type *
            </label>
            <select
              name="appointment_type"
              value={formData.appointment_type}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value={AppointmentType.CHECKUP}>Checkup</option>
              <option value={AppointmentType.EMERGENCY}>Emergency</option>
              <option value={AppointmentType.SURGERY}>Surgery</option>
              <option value={AppointmentType.CONSULTATION}>Consultation</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              placeholder="Appointment details..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Internal Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={2}
              placeholder="Private notes (not visible to pet owner)..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(-1)}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.practice_id || !formData.pet_owner_id || formData.pet_ids.length === 0 || !formData.appointment_date}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Creating...' : 'Create Appointment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentForm;
